import {DownOutlined, InfoCircleOutlined, RightOutlined} from "@ant-design/icons";
import {Button, Dropdown, Form, Input, Space, Tooltip} from "antd";
import classnames from "classnames";
import React, {memo, FC, useState} from "react";
import {EmailVariablesMenu} from "../../../../shared/templates/templateMail.variableTypes";
import {EditTemplateInput, GetTemplateByIdQuery, MailDataType, TemplateType} from "../../../generated/graphql";
import i18n from "../../../services/i18n";
import {getVariableDescription} from "./variableDescriptions";

const DUNNING_TEMPLATE_TYPES = [TemplateType.DunningReminder, TemplateType.DunningLevel1, TemplateType.DunningLevel2, TemplateType.DunningLevel3];

const {TextArea} = Input;

// Union type for template content to support both old and new API types
export type TemplateContent = (GetTemplateByIdQuery["template"] | (EditTemplateInput & {id: string})) & {
    subject?: string | null;
    headerText?: string | null;
    footerText?: string | null;
    signature?: string | null;
};

export type TemplateField = Extract<keyof TemplateContent, string>;

type TemplateInputFieldProps = {
    field: TemplateField;
    templateContent: TemplateContent;
    handleTextChange: (description: TemplateField, content: string) => void;
    minRows?: number;
    mailDataType: MailDataType;
    style?: React.CSSProperties;
    featureLessonBooking?: boolean;
    isNoTemplate?: boolean;
    renderAs?: "div" | "formItem";
    className?: string;
    variablesMenuClassName?: string;
};

const TemplateInputField: FC<TemplateInputFieldProps> = ({
    field,
    templateContent,
    handleTextChange,
    minRows = 1,
    mailDataType,
    style,
    featureLessonBooking = false,
    isNoTemplate = false,
    renderAs = "div",
    className,
    variablesMenuClassName,
}) => {
    // Handle Different Variable Menus -----------------------------------------------------------------------------
    const variableMenu: Array<EmailVariablesMenu> = [];

    // Booker ------------------------------------------------------------------------------------------------------

    const bookerBasicVariables: EmailVariablesMenu = {
        key: "bookerBasicVariables",
        type: "group",
        label: "Bucher",
        children: [
            {label: "Anrede Förmlich", key: "bookerSalutationFormal"},
            {label: "Anrede Informell", key: "bookerSalutation"},
            {label: "Anrede Informell Schweiz (Grüezi) 🇨🇭", key: "bookerSalutationInformelSwiss"},
            {label: "Vorname", key: "bookerFirstName"},
            {label: "Nachname", key: "bookerLastName"},
            {label: "Voller Name", key: "bookerFullName"},
            {label: "Geschlecht", key: "bookerGender"},
            {label: "Geburtsdatum", key: "bookerBirthday"},
            {
                label: "Kundennummer",
                key: "bookerCustomerNumber",
            },
            {label: "Email", key: "bookerEmail"},
            {label: "Anschrift", key: "bookerAddress"},
            {label: "Teilnehmer", key: "bookerAttendees"},
        ],
    };
    const bookerAddressVariables: EmailVariablesMenu = {
        key: "bookerAddressVariables",
        label: "Bucher Details",
        children: [
            {label: "Krankenkasse", key: "bookerHealthInsuranceFund"},
            {label: "Datum Erstkontakt", key: "bookerInitialContact"},
            {label: "Newsletter", key: "bookerNewsLetter"},
            {label: "Anmerkung", key: "bookerAnnotation"},
            {label: "Mobilnummer", key: "bookerMobileNumber"},
            {label: "Festnetznummer", key: "bookerPhoneNumber"},
            {label: "PLZ", key: "bookerPostCode"},
            {label: "Stadt", key: "bookerCity"},
            {label: "Straße", key: "bookerStreet"},
            {label: "Zusatz", key: "bookerAddition"},
            {label: "DSGVO", key: "bookerDSGVO"},
            {label: "Bezahlmehtoden", key: "bookerPaymentMethods"},
        ],
    };

    const bookerSepaVariables: EmailVariablesMenu = {
        key: "bookerSepaVariables",
        label: "Sepa Lastschrift",
        children: [
            {label: "Kontoinhaber", key: "bookerSepaAccountOwner"},
            {label: "IBAN", key: "bookerIban"},
            {label: "Bankname", key: "bookerBankName"},
        ],
    };

    // Attendee ------------------------------------------------------------------------------------------------------

    const attendeeBasicVariables: EmailVariablesMenu = {
        key: "attendeeBasicVariables",
        type: "group",
        label: "Teilnehmer",
        children: [
            {label: "Vorname", key: "attendeeFirstName"},
            {label: "Nachname", key: "attendeeLastName"},
            {label: "Voller Name", key: "attendeeFullName"},
            {label: "Geschlecht", key: "attendeeGender"},
            {label: "Geburtsdatum", key: "attendeeBirthday"},
            {label: "Alter", key: "attendeeAge"},
            // {label: "Zusatz", key: "attendeeAddition"},
            // {label: "Mitglied", key: "attendeeMember"},
            // {label: "Buchungen", key: "attendeeBookings"},
            {label: "Bucher", key: "attendeeBooker"},
        ],
    };

    // Booking ------------------------------------------------------------------------------------------------------

    const bookingBasicVariables: EmailVariablesMenu = {
        key: "bookingBasicVariables",
        label: "Buchung",
        children: [
            {label: "Buchungsnummer", key: "bookingNumber"},
            {label: "Teilnehmer", key: "attendeeFullName"},
            {label: "Bucher", key: "attendeeBooker"},
            {label: "Bruttopreis", key: "grossPrice"},
            // {label: "Nettopreis", key: "netPrice"},
            {label: "Mwst.", key: "bookingTaxRate"},
            {label: "Verrechnetes Guthaben", key: "creditedBalance"},
            {label: "Art der Buchung", key: "bookingType"},
            {label: "Abgerechnet", key: "bookingSettled"},
            {label: "Zuetzt bearbeitet am", key: "bookingLastEdited"},
            {label: "Onlinebuchung", key: "bookingOnlineBooking"},
            {label: "Nachricht des Buchers", key: "noticeBooker"},
            {label: "Zusatz-Info", key: "additionalInfo"},
            {label: "Sonderpreis", key: "bookingSpecialPrice"},
            {label: "Bezahldatum", key: "payDate"},
            {label: "Bezahlmethode", key: "paymentMethod"},
            {label: "Bezahlart (Vorkasse/Moantlich)", key: "paymentType"},
            {label: "Rechnungsnummer", key: "invoiceNumber"},
        ],
    };

    const bookingCourseVariables: EmailVariablesMenu = {
        key: "bookingCourseVariables",
        label: "Kurs",
        children: [
            {label: "Kursnummer", key: "courseNumber"},
            {label: "Kursart", key: "courseType"},
            {label: "Subtitel des Kurses", key: "bookingCourseDescription"},
            {label: "Anmerkungen", key: "bookingCourseAnnotation"},
            {label: "Kursort", key: "location"},
            {label: "Becken/Raum", key: "venue"},
            {label: "Kursstandortname", key: "bookingLocationName"},
            {label: "Straße", key: "bookingLocationStreet"},
            {label: "PLZ", key: "bookingLocationZipCode"},
            {label: "Ort", key: "bookingLocationCity"},
            {label: "Standort-Text für Vorlagen", key: "bookingLocationEmailVariableInfo"},
            {label: "Erste Kurseinheit", key: "firstCourseLesson"},
            {label: "Letzte Kurseinheit", key: "lastCourseLessonDate"},
            {label: "Anz. Kurseinheiten", key: "courseLessonCount"},
            {label: "Kurseinheiten", key: "bookingCourseUnits"},
            {label: "Folgekurse-Link", key: "referenceCoursesLink"},
        ],
    };

    const bookingBookerVariables: EmailVariablesMenu = {
        key: "bookingBookerVariables",
        label: "Bucher",
        children: [
            {label: "Anrede Förmlich", key: "bookerSalutationFormal"},
            {label: "Anrede Informell", key: "bookerSalutation"},
            {label: "Anrede Informell Schweiz", key: "bookerSalutationInformelSwiss"},
            {label: "Vorname", key: "bookerFirstName"},
            {label: "Nachname", key: "bookerLastName"},
            {label: "Voller Name", key: "bookerFullName"},
            {label: "Geschlecht", key: "bookerGender"},
            {label: "Geburtsdatum", key: "bookerBirthday"},
            {label: "Email", key: "bookerEmail"},
            {label: "Anschrift", key: "bookerAddress"},
            {label: "Hausnummer", key: "bookerHouseNumber"},
            {label: "Teilnehmer", key: "bookerAttendees"},
            {label: "Bezahlmethoden", key: "bookerPaymentMethods"},
            {label: "IBAN", key: "bookerIban"},
            {label: "Name der Bank", key: "bookerBankName"},
            {label: "Kontoinhaber", key: "bookerSepaAccountOwner"},
            {label: "Krankenkasse", key: "bookerHealthInsuranceFund"},
            {label: "Datum Erstkontakt", key: "bookerInitialContact"},
            {label: "Newsletter", key: "bookerNewsLetter"},
            {label: "Anmerkung", key: "bookerAnnotation"},
            {label: "Mobilnummer", key: "bookerMobileNumber"},
            {label: "Festnetznummer", key: "bookerPhoneNumber"},
            {label: "PLZ", key: "bookerPostCode"},
            {label: "Stadt", key: "bookerCity"},
            {label: "Straße", key: "bookerStreet"},
            {label: "Zusatz", key: "bookerAddition"},
            {label: "DSGVO", key: "bookerDSGVO"},
        ],
    };

    const bookingAttendeeVariables: EmailVariablesMenu = {
        key: "bookingAttendeeVariables",
        label: "Teilnehmer",
        children: [
            {label: "Voller Name", key: "attendee"},
            {label: "Vorname", key: "attendeeFirstName"},
            {label: "Nachname", key: "attendeeLastName"},
            {label: "Geschlecht", key: "attendeeGender"},
            {label: "Geburtsdatum", key: "attendeeBirthday"},
            {label: "Alter", key: "attendeeAge"},
            {label: "Zusatz", key: "attendeeAddition"},
            {label: "Mitglied", key: "attendeeMember"},
        ],
    };

    const bookingInstructorVariables: EmailVariablesMenu = {
        key: "bookingInstructorVariables",
        label: "Kursleiter",
        children: [
            {label: "Vorname KL 1", key: "instructorFirstName1"},
            {label: "Nachname KL 1", key: "instructorLastName1"},
            {label: "Email KL 1", key: "instructorEmail1"},
            {label: "Telefon KL 1", key: "instructorPhoneNumber1"},
            {label: "Mobil KL 1", key: "instructorMobileNumber1"},
            {label: "Vorname KL 2", key: "instructorFirstName2"},
            {label: "Nachname KL 2", key: "instructorLastName2"},
            {label: "Email KL 2", key: "instructorEmail2"},
            {label: "Telefon KL 2", key: "instructorPhoneNumber2"},
            {label: "Mobil KL 2", key: "instructorMobileNumber2"},
        ],
    };

    const bookingLessonVariables: EmailVariablesMenu = {
        key: "bookingLessonVariables",
        label: "Kurseinheiten",
        children: [
            {label: "Anz. Kurseinheiten", key: "courseLessonCount"},
            {label: "Datum Kurseinheit 01", key: "courseLessonDate01"},
            {label: "Datum Kurseinheit 02", key: "courseLessonDate02"},
            {label: "Datum Kurseinheit 03", key: "courseLessonDate03"},
            {label: "Datum Kurseinheit 04", key: "courseLessonDate04"},
            {label: "Datum Kurseinheit 05", key: "courseLessonDate05"},
            {label: "Datum Kurseinheit 06", key: "courseLessonDate06"},
            {label: "Datum Kurseinheit 07", key: "courseLessonDate07"},
            {label: "Datum Kurseinheit 08", key: "courseLessonDate08"},
            {label: "Datum Kurseinheit 09", key: "courseLessonDate09"},
            {label: "Datum Kurseinheit 10", key: "courseLessonDate10"},
            {label: "Datum Kurseinheit 11", key: "courseLessonDate11"},
            {label: "Datum Kurseinheit 12", key: "courseLessonDate12"},
            {label: "Datum Kurseinheit 13", key: "courseLessonDate13"},
            {label: "Datum Kurseinheit 14", key: "courseLessonDate14"},
            {label: "Datum Kurseinheit 15", key: "courseLessonDate15"},
            {label: "Datum Kurseinheit 16", key: "courseLessonDate16"},
            {label: "Datum Kurseinheit 17", key: "courseLessonDate17"},
            {label: "Datum Kurseinheit 18", key: "courseLessonDate18"},
            {label: "Datum Kurseinheit 19", key: "courseLessonDate19"},
            {label: "Datum Kurseinheit 20", key: "courseLessonDate20"},
        ],
    };

    const bookedLessonVariables: EmailVariablesMenu = {
        key: "bookedLessonVariables",
        label: "Gebuchte Einheiten",
        children: [
            {label: "Anz. Einheiten", key: "bookedCourseLessonCount"},
            {label: "Erste Einheit", key: "firstBookedCourseLesson"},
            {label: "Letzte Einheit", key: "lastBookedCourseLessonDate"},
            {label: "Datum Einheit 01", key: "bookedCourseLessonDate01"},
            {label: "Datum Einheit 02", key: "bookedCourseLessonDate02"},
            {label: "Datum Einheit 03", key: "bookedCourseLessonDate03"},
            {label: "Datum Einheit 04", key: "bookedCourseLessonDate04"},
            {label: "Datum Einheit 05", key: "bookedCourseLessonDate05"},
            {label: "Datum Einheit 06", key: "bookedCourseLessonDate06"},
            {label: "Datum Einheit 07", key: "bookedCourseLessonDate07"},
            {label: "Datum Einheit 08", key: "bookedCourseLessonDate08"},
            {label: "Datum Einheit 09", key: "bookedCourseLessonDate09"},
            {label: "Datum Einheit 10", key: "bookedCourseLessonDate10"},
            {label: "Datum Einheit 11", key: "bookedCourseLessonDate11"},
            {label: "Datum Einheit 12", key: "bookedCourseLessonDate12"},
            {label: "Datum Einheit 13", key: "bookedCourseLessonDate13"},
            {label: "Datum Einheit 14", key: "bookedCourseLessonDate14"},
            {label: "Datum Einheit 15", key: "bookedCourseLessonDate15"},
            {label: "Datum Einheit 16", key: "bookedCourseLessonDate16"},
            {label: "Datum Einheit 17", key: "bookedCourseLessonDate17"},
            {label: "Datum Einheit 18", key: "bookedCourseLessonDate18"},
            {label: "Datum Einheit 19", key: "bookedCourseLessonDate19"},
            {label: "Datum Einheit 20", key: "bookedCourseLessonDate20"},
        ],
    };

    const bookingScheduledPaymentVariables: EmailVariablesMenu = {
        key: "bookingScheduledPaymentVariables",
        label: "Monatliche Raten",
        children: [
            {label: "Anz. Raten", key: "scheduledPaymentCount"},
            {label: "Monatsrate 01 Betrag", key: "scheduledPayments1amount"},
            {label: "Monatsrate 01 Datum", key: "scheduledPayments1due"},
            {label: "Monatsrate 02 Betrag", key: "scheduledPayments2amount"},
            {label: "Monatsrate 02 Datum", key: "scheduledPayments2due"},
            {label: "Monatsrate 03 Betrag", key: "scheduledPayments3amount"},
            {label: "Monatsrate 03 Datum", key: "scheduledPayments3due"},
            {label: "Monatsrate 04 Betrag", key: "scheduledPayments4amount"},
            {label: "Monatsrate 04 Datum", key: "scheduledPayments4due"},
            {label: "Monatsrate 05 Betrag", key: "scheduledPayments5amount"},
            {label: "Monatsrate 05 Datum", key: "scheduledPayments5due"},
            {label: "Monatsrate 06 Betrag", key: "scheduledPayments6amount"},
            {label: "Monatsrate 06 Datum", key: "scheduledPayments6due"},
            {label: "Monatsrate 07 Betrag", key: "scheduledPayments7amount"},
            {label: "Monatsrate 07 Datum", key: "scheduledPayments7due"},
            {label: "Monatsrate 08 Betrag", key: "scheduledPayments8amount"},
            {label: "Monatsrate 08 Datum", key: "scheduledPayments8due"},
            {label: "Monatsrate 09 Betrag", key: "scheduledPayments9amount"},
            {label: "Monatsrate 09 Datum", key: "scheduledPayments9due"},
            {label: "Monatsrate 10 Betrag", key: "scheduledPayments10amount"},
            {label: "Monatsrate 10 Datum", key: "scheduledPayments10due"},
            {label: "Monatsrate 11 Betrag", key: "scheduledPayments11amount"},
            {label: "Monatsrate 11 Datum", key: "scheduledPayments11due"},
            {label: "Monatsrate 12 Betrag", key: "scheduledPayments12amount"},
            {label: "Monatsrate 12 Datum", key: "scheduledPayments12due"},
            {label: "Ratenübersicht", key: "scheduledPaymentsOverview"},
        ],
    };

    // Rechnung / Zahlung (ADR 0004: bisher ersetzt, aber in keinem Menü) ----------------------------------------

    const bookingInvoiceVariables: EmailVariablesMenu = {
        key: "bookingInvoiceVariables",
        label: "Rechnung",
        children: [
            {label: "Rechnungsdatum", key: "invoiceCreateDate"},
            {label: "Rechnungsnummer (Storno)", key: "bookingRNumber"},
            {label: "Rechnung verbucht", key: "billed"},
            {label: "Rechnungsempfänger Firma", key: "invoiceCompanyName"},
            {label: "Rechnungsempfänger Vorname", key: "invoiceFirstName"},
            {label: "Rechnungsempfänger Nachname", key: "invoiceLastName"},
            {label: "Rechnungsadresse Zusatz", key: "invoiceAddition"},
            {label: "Rechnungsadresse Straße", key: "invoiceStreet"},
            {label: "Rechnungsadresse PLZ", key: "invoicePostCode"},
            {label: "Rechnungsadresse Ort", key: "invoiceCity"},
        ],
    };

    const bookingPaymentVariables: EmailVariablesMenu = {
        key: "bookingPaymentVariables",
        label: "Zahlung",
        children: [
            {label: "Zahlungslink (Mollie)", key: "molliePaymentUrl"},
            {label: "Überweisung Empfänger", key: "mollieBankTransferRecipient"},
            {label: "Überweisung IBAN", key: "mollieBankTransferIBAN"},
            {label: "Überweisung BIC", key: "mollieBankTransferBIC"},
            {label: "Überweisung Verwendungszweck", key: "mollieBankTransferReference"},
            {label: "Überweisung Betrag", key: "mollieBankTransferAmount"},
            {label: "Überweisung fällig bis", key: "mollieBankTransferDueDate"},
            {label: "Bezahlt", key: "paid"},
            {label: "Bezahlt am", key: "paidDate"},
            {label: "Alle Raten bezahlt", key: "allPaid"},
            {label: "Alle Raten bezahlt am", key: "allPaidDate"},
            {label: "QR-Referenz (Schweiz)", key: "qrReference"},
            {label: "Mandatsreferenz", key: "mandateReference"},
            {label: "IBAN der Buchung", key: "bookingIban"},
            {label: "Bank der Buchung", key: "bookingBankName"},
            {label: "Kontoinhaber der Buchung", key: "bookingSepaAccountOwner"},
            {label: "Rabatt", key: "discount"},
            {label: "Rabattname", key: "discountName"},
            {label: "Mitgliedsrabatt", key: "memberDiscountAmount"},
            {label: "Kursgebühr vor Abzügen", key: "originalCourseFee"},
            {label: "Erstattet", key: "refundTotal"},
            {label: "MwSt.-Satz", key: "vat"},
            {label: "MwSt.-Betrag", key: "vatAmount"},
        ],
    };

    // Firma (ADR 0004: für alle Empfängertypen, auch Mitarbeiter) ------------------------------------------------

    const companyVariables: EmailVariablesMenu = {
        key: "companyVariables",
        label: "Firma",
        children: [
            {label: "Firmenname", key: "organizationName"},
            {label: "Inhaber", key: "organizationOwner"},
            {label: "Straße", key: "organizationStreet"},
            {label: "Hausnummer", key: "organizationStreetNumber"},
            {label: "PLZ", key: "organizationZipCode"},
            {label: "Ort", key: "organizationCity"},
            {label: "Telefon", key: "organizationPhone"},
            {label: "Fax", key: "organizationFax"},
            {label: "E-Mail", key: "organizationEmail"},
            {label: "Website", key: "organizationWebsite"},
            {label: "USt-IdNr.", key: "organizationTaxId"},
            {label: "Steuernummer", key: "organizationTaxNumber"},
        ],
    };

    // Interessenten-Bestätigung — eigener Variablen-Satz (vom Server aufgelöst).
    const interestVariables: EmailVariablesMenu = {
        key: "interestVariables",
        type: "group",
        label: "Interessenten-Bekundung",
        children: [
            {label: "Kursart", key: "courseType"},
            {label: "Standort", key: "location"},
            {label: "Vorname Bucher", key: "bookerFirstName"},
            {label: "Vorname Teilnehmer", key: "attendeeFirstName"},
            {label: "Nachname Teilnehmer", key: "attendeeLastName"},
            {label: "Gewählte Zeitfenster", key: "selectedTimeSlots"},
            {label: "Anmerkung", key: "note"},
        ],
    };

    // Umbuchung (#664, ADR 0006) — zusätzlich zu den Buchungs-Variablen, aus Buchung B + Übergabe aufgelöst.
    const transferVariables: EmailVariablesMenu = {
        key: "transferVariables",
        type: "group",
        label: "Umbuchung",
        children: [
            {label: "Alte Buchungsnummer", key: "transferOldBookingNumber"},
            {label: "Neue Buchungsnummer", key: "transferNewBookingNumber"},
            {label: "Alter Kurs", key: "transferOldCourse"},
            {label: "Zeitraum alter Kurs", key: "transferOldCoursePeriod"},
            {label: "Neuer Kurs", key: "transferNewCourse"},
            {label: "Zeitraum neuer Kurs", key: "transferNewCoursePeriod"},
            {label: "Gezahlt (alte Buchung)", key: "transferPaidAmount"},
            {label: "Verrechnet (neue Buchung)", key: "transferAppliedAmount"},
            {label: "Überschuss (bleibt Guthaben)", key: "transferCreditAmount"},
            {label: "Guthaben-Saldo danach", key: "transferCreditBalance"},
            {label: "Guthaben-Hinweis", key: "transferCreditNote"},
            {label: "Offener Betrag (neue Buchung)", key: "transferOpenAmount"},
            {label: "Fälligkeit offener Betrag", key: "transferOpenDue"},
            {label: "Hinweis übernommene Posten und offener Betrag", key: "transferOpenNote"},
            {label: "Hinweis Rabatt nicht übernommen", key: "transferDiscountNote"},
        ],
    };

    // Mahnung (#1517) — zusätzlich zu den Buchungs-Variablen, aus dunningTextVariables im Mahn-Service.
    const dunningVariables: EmailVariablesMenu = {
        key: "dunningVariables",
        type: "group",
        label: "Mahnung",
        children: [
            {label: "Mahnnummer", key: "dunningNumber"},
            {label: "Mahnstufe", key: "dunningLevel"},
            {label: "Offener Betrag", key: "dunningOpenAmount"},
            {label: "Mahngebühr", key: "dunningFeeAmount"},
            {label: "Gesamtbetrag", key: "dunningTotalAmount"},
            {label: "Neue Zahlungsfrist", key: "dunningDueDate"},
            {label: "Frühere Mahnungen", key: "dunningPreviousDates"},
            {label: "Bankverbindung", key: "dunningBankAccount"},
            {label: "Verwendungszweck", key: "dunningPaymentReference"},
        ],
    };

    // VariablesMenu ------------------------------------------------------------------------------------------------------

    // Interessenten-Bestätigung nutzt einen eigenen, festen Variablen-Satz
    // (unabhängig vom mailDataType-Mapping der übrigen Vorlagen).
    // `templateType` ist nicht in jedem Zweig des TemplateContent-Union-Typs
    // vorhanden, daher defensiv ausgelesen.
    const currentTemplateType = (templateContent as {templateType?: TemplateType}).templateType;
    const isInterestConfirmation = currentTemplateType === TemplateType.InterestListConfirmation;

    // „Überweisungsdaten gültig bis" braucht das Mollie-expiresAt des aktuellen Attempts,
    // das nur die Rechnungs-Pfade hydrieren (hydrateMollieAttemptExpiry). In Buchungs-,
    // Kurs- und Raten-Mails bliebe die Variable leer, deshalb nur in Rechnungsvorlagen.
    if (currentTemplateType === TemplateType.Invoice || currentTemplateType === TemplateType.InvoiceMonthly || currentTemplateType === TemplateType.InvoiceMonthlyConsistent) {
        bookingPaymentVariables.children.push({label: "Überweisungsdaten gültig bis", key: "mollieBankTransferValidUntil"});
        // Datum der Fälligkeitszeile; entsteht erst beim Rendern der Rechnung (fillPaymentDueInTextBlocks).
        bookingInvoiceVariables.children.splice(1, 0, {label: "Fälligkeitsdatum", key: "paymentDue"});
    }

    if (currentTemplateType === TemplateType.Transfer) {
        variableMenu.push(transferVariables);
    }

    if (currentTemplateType && DUNNING_TEMPLATE_TYPES.includes(currentTemplateType)) {
        variableMenu.push(dunningVariables);
    }

    if (isInterestConfirmation) {
        // Nur Variablen anbieten, die der Interessenten-Zweig in textBlockReplacement.ts
        // auch auflöst (#842). Der auf
        // 'Booking' gesetzte mailDataType würde sonst Kurs-/Raten-/Lektions-
        // Variablen zeigen, die es ohne Buchung nicht gibt — sie blieben roh in
        // der versendeten Mail stehen.
        variableMenu.push(interestVariables, bookerBasicVariables, bookerAddressVariables, attendeeBasicVariables);
    }

    switch (isInterestConfirmation ? undefined : mailDataType) {
        case MailDataType.Booker:
            variableMenu.push(bookerBasicVariables, bookerAddressVariables, bookerSepaVariables, companyVariables);
            break;
        case MailDataType.Attendee:
            variableMenu.push(attendeeBasicVariables, companyVariables);
            break;

        case MailDataType.Booking:
            variableMenu.push(
                bookingBasicVariables,
                bookingCourseVariables,
                bookingBookerVariables,
                bookingAttendeeVariables,
                bookingInstructorVariables,
                bookingLessonVariables,
                bookingScheduledPaymentVariables,
            );

            if (featureLessonBooking) {
                variableMenu.push(bookedLessonVariables);
            }
            variableMenu.push(bookingInvoiceVariables, bookingPaymentVariables, companyVariables);
            break;

        case MailDataType.Course:
            variableMenu.push(
                bookingBasicVariables,
                bookingCourseVariables,
                bookingBookerVariables,
                bookingAttendeeVariables,
                bookingInstructorVariables,
                bookingLessonVariables,
                bookingInvoiceVariables,
                bookingPaymentVariables,
                companyVariables,
            );
            break;

        case MailDataType.AttendanceList:
            variableMenu.push(bookingCourseVariables, bookingInstructorVariables, bookingLessonVariables);
            break;

        case MailDataType.ScheduledPayment:
            variableMenu.push(
                bookingBasicVariables,
                bookingCourseVariables,
                bookingBookerVariables,
                bookingAttendeeVariables,
                bookingInstructorVariables,
                bookingLessonVariables,
                bookingScheduledPaymentVariables,
                bookingInvoiceVariables,
                bookingPaymentVariables,
                companyVariables,
            );
            break;

        case MailDataType.Staff:
            variableMenu.push(companyVariables);
            break;

        default:
            break;
    }

    // Variable Menus -----------------------------------------------------------------------------

    // Paste variable at cursor position
    const [cursorPosition, setCursorPosition] = useState<number>(0);

    const updateCursorPosition = (e: React.MouseEvent<HTMLTextAreaElement, MouseEvent> | React.KeyboardEvent<HTMLTextAreaElement>) => {
        setCursorPosition(e.currentTarget.selectionStart);
    };

    const insertText = (inputText: string, variable: string, cursorPosition: number) => {
        const textToInsert = `{{ ${variable} }}`;
        const textBeforeCursorPosition = inputText.substring(0, cursorPosition);
        const textAfterCursorPosition = inputText.substring(cursorPosition, inputText.length);

        return textBeforeCursorPosition + textToInsert + textAfterCursorPosition;
    };

    const VariablesMenu = ({field, inputText}: {field: TemplateField; inputText: string | undefined}) => {
        const [searchTerm, setSearchTerm] = useState("");
        const [dropdownVisible, setDropdownVisible] = useState(false);
        // Alle Gruppen initial eingeklappt
        const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());

        // Filtere Variablen basierend auf Suchbegriff
        const filteredMenu = React.useMemo(() => {
            if (!searchTerm) {
                return variableMenu;
            }

            const lowerSearch = searchTerm.toLowerCase();

            return variableMenu
                .map((group) => ({
                    ...group,
                    children: (group.children || []).filter((child) => child.label.toLowerCase().includes(lowerSearch) || child.key.toLowerCase().includes(lowerSearch)),
                }))
                .filter((group) => group.children.length > 0);
        }, [searchTerm]);

        const handleVariableClick = (key: string) => {
            const inputTextValue = inputText ?? "";
            const content = insertText(inputTextValue, key, cursorPosition);

            handleTextChange(field, content);
            setDropdownVisible(false);
            setSearchTerm("");
        };

        const toggleGroup = (groupKey: string) => {
            const newExpanded = new Set(expandedGroups);

            if (newExpanded.has(groupKey)) {
                newExpanded.delete(groupKey);
            } else {
                newExpanded.add(groupKey);
            }
            setExpandedGroups(newExpanded);
        };

        // Accordion-Modus: Collapsible Groups (ohne Suche)
        const renderAccordionMode = (items: typeof variableMenu) => {
            return items.map((group) => {
                const isExpanded = expandedGroups.has(group.key);

                return (
                    <div key={group.key} style={{marginBottom: 4}}>
                        <div
                            onClick={() => toggleGroup(group.key)}
                            onKeyDown={(e) => {
                                if (e.key === "Enter" || e.key === " ") {
                                    toggleGroup(group.key);
                                }
                            }}
                            role="button"
                            tabIndex={0}
                            style={{
                                padding: "8px 12px",
                                fontWeight: 600,
                                fontSize: 13,
                                color: "#333",
                                background: "#f5f5f5",
                                cursor: "pointer",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                borderRadius: 4,
                                transition: "background 0.2s",
                            }}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.background = "#e8e8e8";
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.background = "#f5f5f5";
                            }}
                        >
                            <span>{group.label}</span>
                            <span style={{fontSize: 10, color: "#999", transition: "transform 0.2s", transform: isExpanded ? "rotate(90deg)" : "rotate(0deg)"}}>
                                <RightOutlined />
                            </span>
                        </div>
                        {isExpanded && (
                            <div style={{paddingLeft: 8}}>
                                {group.children.map((child) => (
                                    <div
                                        key={child.key}
                                        onClick={() => handleVariableClick(child.key)}
                                        onKeyDown={(e) => {
                                            if (e.key === "Enter" || e.key === " ") {
                                                handleVariableClick(child.key);
                                            }
                                        }}
                                        role="button"
                                        tabIndex={0}
                                        style={{
                                            padding: "8px 12px",
                                            cursor: "pointer",
                                            transition: "background 0.2s",
                                            borderLeft: "2px solid #e8e8e8",
                                        }}
                                        onMouseEnter={(e) => {
                                            e.currentTarget.style.background = "#f9f9f9";
                                            e.currentTarget.style.borderLeftColor = "#1890ff";
                                        }}
                                        onMouseLeave={(e) => {
                                            e.currentTarget.style.background = "transparent";
                                            e.currentTarget.style.borderLeftColor = "#e8e8e8";
                                        }}
                                    >
                                        <div style={{display: "flex", alignItems: "center", gap: 6}}>
                                            {/* <Tooltip title={getVariableDescription(child.key)}>
                                                <InfoCircleOutlined style={{color: "#999", fontSize: 12, flexShrink: 0, cursor: "help"}} />
                                            </Tooltip> */}
                                            <span style={{fontWeight: 500, fontSize: 13}}>{child.label}</span>
                                        </div>
                                        {/* <div style={{fontSize: 11, color: "#999", marginTop: 4, marginLeft: 18}}>{`{{ ${child.key} }}`}</div> */}
                                        <div style={{fontSize: 11, color: "#999", marginTop: 0, marginLeft: 0}}>{getVariableDescription(child.key)}</div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                );
            });
        };

        // Flat-Modus: Alle Gruppen aufgeklappt (mit Suche)
        const renderFlatMode = (items: typeof filteredMenu) => {
            return items.map((group) => (
                <div key={group.key} style={{marginBottom: 8}}>
                    <div
                        style={{
                            padding: "6px 12px",
                            fontWeight: 600,
                            fontSize: 11,
                            color: "#666",
                            textTransform: "uppercase" as const,
                            background: "#f5f5f5",
                            borderRadius: 4,
                        }}
                    >
                        {group.label}
                    </div>
                    {group.children.map((child) => (
                        <div
                            key={child.key}
                            onClick={() => handleVariableClick(child.key)}
                            onKeyDown={(e) => {
                                if (e.key === "Enter" || e.key === " ") {
                                    handleVariableClick(child.key);
                                }
                            }}
                            role="button"
                            tabIndex={0}
                            style={{
                                padding: "8px 12px",
                                cursor: "pointer",
                                transition: "background 0.2s",
                            }}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.background = "#f0f0f0";
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.background = "transparent";
                            }}
                        >
                            <div style={{display: "flex", alignItems: "center", gap: 6}}>
                                <Tooltip title={getVariableDescription(child.key)}>
                                    <InfoCircleOutlined style={{color: "#999", fontSize: 12, flexShrink: 0, cursor: "help"}} />
                                </Tooltip>
                                <span style={{fontWeight: 500}}>{child.label}</span>
                            </div>
                            <div style={{fontSize: 11, color: "#999", marginTop: 4, marginLeft: 18}}>{`{{ ${child.key} }}`}</div>
                        </div>
                    ))}
                </div>
            ));
        };

        const hasSearchTerm = searchTerm.trim().length > 0;

        const dropdownContent = (
            <div style={{background: "#fff", boxShadow: "0 3px 6px rgba(0,0,0,0.16)", padding: 8, borderRadius: 8, minWidth: 320, maxWidth: 400}}>
                <Input placeholder="Variable suchen..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} style={{marginBottom: 8}} allowClear onClick={(e) => e.stopPropagation()} />
                <div style={{maxHeight: 500, overflowY: "auto", paddingRight: 4}}>
                    {hasSearchTerm ? (
                        // Such-Modus: Flache gefilterte Liste
                        filteredMenu.length > 0 ? (
                            renderFlatMode(filteredMenu)
                        ) : (
                            <div style={{padding: "24px 12px", color: "#999", textAlign: "center"}}>{"Keine Variablen gefunden"}</div>
                        )
                    ) : (
                        // Browse-Modus: Accordion mit zusammenklappbaren Gruppen
                        <div style={{paddingTop: 4}}>{renderAccordionMode(variableMenu)}</div>
                    )}
                </div>
            </div>
        );

        return (
            <Dropdown
                dropdownRender={() => dropdownContent}
                trigger={["click"]}
                open={dropdownVisible}
                onOpenChange={(visible) => {
                    setDropdownVisible(visible);
                    if (!visible) {
                        setSearchTerm("");
                    }
                }}
                className={isNoTemplate ? undefined : variablesMenuClassName}
            >
                <Button type="link">
                    <Space>
                        {"Variablen"}
                        <DownOutlined />
                    </Space>
                </Button>
            </Dropdown>
        );
    };

    // Return -----------------------------------------------------------------------------

    const inputField = (field: TemplateField) => {
        const inputText = (templateContent[field as keyof typeof templateContent] ?? "") as string;
        const fieldKey = field as keyof typeof i18n.containers.templates.EmailTemplates.sections;
        const label = typeof i18n.containers.templates.EmailTemplates.sections[fieldKey] === "function" ? (i18n.containers.templates.EmailTemplates.sections[fieldKey] as () => string)() : field;

        const textArea = (
            <TextArea
                key={field}
                className={classnames(className)}
                placeholder={i18n.views.EditTextField.placeholder()}
                allowClear
                value={(templateContent[field as keyof typeof templateContent] ?? "") as string}
                onChange={(e) => {
                    handleTextChange(field, e.target.value);
                    setCursorPosition(e.target.selectionStart);
                }}
                onClick={updateCursorPosition}
                onKeyUp={updateCursorPosition}
                autoSize={{minRows, maxRows: 20}}
            />
        );

        if (renderAs === "formItem") {
            return (
                <>
                    <VariablesMenu field={field} inputText={inputText} />
                    <Form.Item label={label} key={field}>
                        {textArea}
                    </Form.Item>
                </>
            );
        }

        return (
            <div key={field} style={{margin: 20, ...style}}>
                <h4>{label}</h4>
                <VariablesMenu field={field} inputText={inputText} />
                {textArea}
            </div>
        );
    };

    return inputField(field);
};

export default memo(TemplateInputField);
